import { useEffect } from "react";
import { useRecoilState } from "recoil";
import { getRecoil } from "recoil-nexus";
import _ from "lodash";

import { BetTypeEnum, BetTypeViewEnum } from "src/constants/enums/betType";
import { toastWrapper } from "src/common/components/toastWrapper";
import { betTypeFitsConfig } from "src/common/helpers/betType";
import { isTerminal } from "src/common/helpers/productType";
import { ISelection } from "src/constants/interfaces/bet";
import selectionsStore from "src/stores/selections";
import { getMaxStake } from "src/services/maxStake";
import mainStore from "src/stores/main";
import { usePrevious } from "./previous";
import { useBalance } from "./balance";
import { betTypeFitsConfig } from "src/common/helpers/betType";
import { getBetBuilderGameIds, getEffectiveSelectionsCount } from "src/common/helpers/betBuilder";
import { useConfig } from "./config";

const betTypesWithMaxStake = [BetTypeEnum.Single, BetTypeEnum.Multiple, BetTypeEnum.System];

export const useSelectionsSync = (
  appSelections: ISelection[],
  appLastBetSelections: ISelection[],
  onSelectionsChange: (selections: ISelection[]) => void
) => {
  const config = useConfig();
  const [{ selections }, setSelections] = useRecoilState(selectionsStore);
  const [{ betType, stake, systemCombinationCount }, setMainState] = useRecoilState(mainStore);

  const balance = useBalance();
  const eventIds = selections.map((item) => item.event.id);
  const prevEventIds = usePrevious(eventIds);
  const prevBetType = usePrevious(betType);
  const prevSystemCombinationCount = usePrevious(systemCombinationCount);

  const betTypeConfig = config.availableBetTypes[BetTypeViewEnum[betType]]
  const betTypeMaxSelectionsCount = (betTypeConfig?.maxSelectionsCount || Number.MAX_SAFE_INTEGER)

  useEffect(() => {
    // Bet builder cards collapse to 1 each in count limits — a single card
    // with 5 events still counts as 1 selection toward max-count rules.
    const effectiveCount = getEffectiveSelectionsCount(appSelections);

    if (effectiveCount > config.maxSelectionsCount) {
      toastWrapper("error", `Max selection count is {1}`, config.maxSelectionsCount);
      return onSelectionsChange(appSelections.filter((_, ind) => ind !== appSelections.length - 1));
    }
    if (betTypeConfig?.blockAutoFixOfBetType && effectiveCount > betTypeMaxSelectionsCount) {
      toastWrapper('error', betTypeConfig?.maxErrorMessage || 'Max selection count is {1}', betTypeConfig?.maxSelectionsCount)

      return onSelectionsChange(appSelections.filter((_, ind) => ind !== appSelections.length - 1));
    }
    setSelections(({ selections }) => {
      const selectionsCountChanged = Object.values(selections).length !== appSelections.length;
      const changedSelections = appSelections.map((appSelection) => {
        const existing = selections.find(({ event: storedEvent }) => storedEvent.id === appSelection.event.id);
        const isBanker = appSelection.isBanker ?? (selectionsCountChanged ? false : existing?.isBanker);

        return existing
          ? {
            ...existing,
            isBanker: !!isBanker,
            event: {
              ...existing.event,
              ...appSelection.event,
              // Preserve the BB tag: once a leg is part of a BB card, keep it
              // tagged even if a re-sync from sportsbook arrives without the
              // flag (sportsbook only sets it for picks from game details).
              isInternalBetBuilder:
                existing.event.isInternalBetBuilder || appSelection.event.isInternalBetBuilder,
              price: existing.loaded ? existing.event.price : appSelection.event.price
            },
          }
          : appSelection;
      });

      // If the user already has a BB card open for a game (i.e. at least one
      // leg from that game is tagged BB), any newly added selection from the
      // same game — including picks made OUTSIDE game details (competition
      // page, search, etc.) — should also be treated as part of that card.
      // Sportsbook itself only tags picks made from game details, so without
      // this we'd add a regular leg that conflicts with the BB card.
      const bbGameIds = getBetBuilderGameIds(changedSelections);
      const finalSelections = bbGameIds.size
        ? changedSelections.map((sel) => (
          bbGameIds.has(sel.game.id) && !sel.event.isInternalBetBuilder
            ? { ...sel, event: { ...sel.event, isInternalBetBuilder: true } }
            : sel
        ))
        : changedSelections;

      return { selections: finalSelections, lastBetSelections: appLastBetSelections };
    });
  }, [appSelections, appLastBetSelections, setSelections]);

  useEffect(() => {
    if (!selections.length && !appSelections.length && stake)
      setMainState((mainState) => ({ ...mainState, stake: "" }));
  }, [selections, appSelections, stake]);

  useEffect(() => {
    if (!eventIds.length) return;

    const betTypeChanged = prevBetType !== betType;
    const eventsChanged = !_.isEqual(eventIds, prevEventIds);
    const validBetType = betTypeFitsConfig(config, selections, betType);

    const needToCallMaxStake = (eventsChanged || prevSystemCombinationCount !== systemCombinationCount) && validBetType;

    if (betTypeChanged && !betTypesWithMaxStake.includes(betType) && isTerminal(config))
      setMainState((mainState) => ({ ...mainState, maxStake: balance }));
    else if (needToCallMaxStake || betTypeChanged) {
      getMaxStake(eventIds, betType, systemCombinationCount)
        .then(maxStake => {
          if (betTypesWithMaxStake.includes(getRecoil(mainStore).betType)) {
            setMainState((mainState) => ({ ...mainState, maxStake }));
          }
        })
        .catch(_.noop);
    }
  }, [config, selections, eventIds, prevEventIds, betType, prevBetType, balance, setMainState, prevSystemCombinationCount, systemCombinationCount]);
};
